import { useState, useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Save, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Roles() {
  const { can } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [perms, setPerms] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
    setRoles(r.data); setCatalog(p.data);
    if (!selected && r.data.length) selectRole(r.data[0]);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const selectRole = (role) => { setSelected(role); setPerms(new Set(role.permissions || [])); };

  const toggle = (perm) => setPerms((s) => { const n = new Set(s); n.has(perm) ? n.delete(perm) : n.add(perm); return n; });
  const toggleModule = (mod) => setPerms((s) => {
    const n = new Set(s);
    const all = mod.permissions.every((p) => n.has(p));
    mod.permissions.forEach((p) => all ? n.delete(p) : n.add(p));
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${selected.id}`, { permissions: Array.from(perms) });
      toast.success("Permissions updated"); load();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const isAdmin = selected?.name === "admin";

  return (
    <div>
      <PageHeader title="Roles & Permissions" subtitle="RBAC — module and action level access control" icon={ShieldCheck}
        breadcrumb={["Home", "Administration", "Roles"]} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-2">
          {roles.map((r) => (
            <button key={r.id} data-testid={`role-${r.name}`} onClick={() => selectRole(r)}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                selected?.id === r.id ? "border-blue-600 bg-blue-50 dark:bg-blue-500/10" : "border-slate-200 dark:border-border bg-white dark:bg-card hover:bg-slate-50"
              }`}>
              <div className="font-display font-semibold capitalize text-slate-800 dark:text-foreground">{r.name.replace("_", " ")}</div>
              <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>
              <div className="mt-2"><StatusBadge status={`${(r.permissions || []).length} perms`} /></div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm">
          {selected && (
            <>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-display text-xl font-bold capitalize text-slate-900 dark:text-foreground">{selected.name.replace("_", " ")}</h3>
                  <p className="text-sm text-slate-500">{isAdmin ? "Full system access (all permissions)" : "Toggle module & action permissions"}</p>
                </div>
                {can("roles.update") && !isAdmin && (
                  <Button data-testid="save-perms-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {catalog.map((mod) => {
                  const allChecked = isAdmin || mod.permissions.every((p) => perms.has(p));
                  return (
                    <div key={mod.module} className="border border-slate-200 dark:border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100 dark:border-border">
                        <Checkbox checked={allChecked} disabled={isAdmin} onCheckedChange={() => toggleModule(mod)} data-testid={`mod-${mod.module}`} />
                        <span className="font-semibold capitalize text-slate-800 dark:text-foreground">{mod.module}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {mod.actions.map((a) => {
                          const perm = `${mod.module}.${a}`;
                          return (
                            <label key={perm} className="flex items-center gap-2 text-sm text-slate-600 dark:text-muted-foreground cursor-pointer">
                              <Checkbox checked={isAdmin || perms.has(perm)} disabled={isAdmin} onCheckedChange={() => toggle(perm)} />
                              <span className="capitalize">{a}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
